import type { InboundCall, SimilarityQuery } from "./queries";

/**
 * The rules of the capture window, apart from the components that draw it.
 *
 * What is asked of every caller, when there is enough typed to be worth a
 * search, and which button a row gets. The server holds the same lines
 * (`crm.services.reception`); these only stop the screen offering what would
 * be refused.
 */

/** The five things reception asks. `location` is a catalogue id, as text. */
export type CallDraft = {
  contactName: string;
  companyName: string;
  phone: string;
  email: string;
  location: string;
};

export const EMPTY_DRAFT: CallDraft = {
  contactName: "",
  companyName: "",
  phone: "",
  email: "",
  location: "",
};

/**
 * Asked of every caller: a name, a company, and a way to ring them back —
 * `reach`, a number or an address, whichever they gave. The city is not.
 */
export type RequiredField = "contactName" | "companyName" | "reach";

const MIN_NAME_LETTERS = 3;
const MIN_EMAIL_LETTERS = 3;
/** Enough of a number to search on while the rest is still being typed. */
const MIN_PHONE_DIGITS_TO_SEARCH = 7;
/** A Mexican number, without its country code. */
const NATIONAL_DIGITS = 10;
/** Enough of an address to take: something, an at, a domain with a dot. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digits(text: string): string {
  return text.replace(/\D/g, "");
}

function hasPhone(draft: CallDraft): boolean {
  return digits(draft.phone).length >= NATIONAL_DIGITS;
}

function hasEmail(draft: CallDraft): boolean {
  return EMAIL.test(draft.email.trim());
}

/** Which of the three is still missing, in the order the form asks them. */
export function missingFields(draft: CallDraft): RequiredField[] {
  const missing: RequiredField[] = [];
  if (!draft.contactName.trim()) missing.push("contactName");
  if (!draft.companyName.trim()) missing.push("companyName");
  if (!hasPhone(draft) && !hasEmail(draft)) missing.push("reach");
  return missing;
}

/** What the search is asked: only the fields with enough in them to mean much. */
export function searchQuery(draft: CallDraft): SimilarityQuery {
  const companyName = draft.companyName.trim();
  const contactName = draft.contactName.trim();
  const email = draft.email.trim();
  return {
    company_name: companyName.length >= MIN_NAME_LETTERS ? companyName : "",
    contact_name: contactName.length >= MIN_NAME_LETTERS ? contactName : "",
    phone:
      digits(draft.phone).length >= MIN_PHONE_DIGITS_TO_SEARCH
        ? draft.phone.trim()
        : "",
    email: email.length >= MIN_EMAIL_LETTERS ? email : "",
  };
}

export function isWorthSearching(query: SimilarityQuery): boolean {
  return Object.values(query).some(Boolean);
}

/** Lower case, no accents, single spaces: how two spellings of a name compare. */
export function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Whether the book already holds a company under exactly this name.
 *
 * Exactly, give or take case and accents — the same test the server makes
 * before it creates one. A merely similar name leaves *Crear empresa* open:
 * that is what the lists above it are for, and reception is the judge.
 */
export function isAlreadyACompany(
  typed: string,
  companies: readonly { name: string; legal_name: string }[],
): boolean {
  const wanted = fold(typed);
  if (!wanted) return false;
  return companies.some(
    (company) => fold(company.name) === wanted || fold(company.legal_name) === wanted,
  );
}

/**
 * Both names a company goes by, legal one first: "Aceros Mexicanos SA de CV |
 * Aceros MX". One where they are the same, give or take case and accents, or
 * where the other is missing — a name said twice is not more information.
 */
export function companyNames(company: { name: string; legal_name: string }): string {
  const legal = company.legal_name.trim();
  const name = company.name.trim();
  if (!legal || !name || fold(legal) === fold(name)) return legal || name;
  return `${legal} | ${name}`;
}

/** The five fields as the server takes them, plus what was decided. */
export function callBody(
  draft: CallDraft,
  decided: { agent: number | null; company?: number; contact?: number },
): InboundCall {
  return {
    contact_name: draft.contactName.trim(),
    company_name: draft.companyName.trim(),
    phone: draft.phone.trim(),
    email: draft.email.trim(),
    location: draft.location ? Number(draft.location) : null,
    agent: decided.agent,
    company: decided.company ?? null,
    contact: decided.contact ?? null,
  };
}

/**
 * Why a call cannot be handed over yet, or `null` when it can.
 *
 * A company somebody holds needs no agent picked: the call is its holder's,
 * whoever reception had in mind. Everything else waits for the select.
 *
 * Number and address may each be left empty, but not half typed: the half one
 * would go out with the call, and the server would refuse the call over it.
 */
export type Blocker = "incomplete" | "badPhone" | "badEmail" | "noAgent";

export function blockerFor(
  draft: CallDraft,
  agent: number | null,
  company: { current_agent: number | null } | null,
): Blocker | null {
  if (missingFields(draft).length > 0) return "incomplete";
  if (draft.phone.trim() && !hasPhone(draft)) return "badPhone";
  if (draft.email.trim() && !hasEmail(draft)) return "badEmail";
  const held = company !== null && company.current_agent !== null;
  if (!held && agent === null) return "noAgent";
  return null;
}
